// Single source of truth for all permissions in the admin panel.
//
// IMPORTANT: `resource` values MUST match the first argument of @RequirePermission() decorators
// in admin-tribe-backend-api/src/modules/**/controllers/*.controller.ts
//
// When adding a new @RequirePermission('newresource', 'action') to the backend,
// add a corresponding entry to PERMISSION_MATRIX here in the same commit.

export type PermissionAction = 'read' | 'create' | 'update' | 'delete' | 'write';

export interface ResourceDefinition {
  resource: string;
  label: string;
  actions: PermissionAction[];
}

export const PERMISSION_MATRIX: ResourceDefinition[] = [
  { resource: 'user',          label: 'Users',         actions: ['read', 'update', 'delete'] },
  { resource: 'content',       label: 'Content',        actions: ['read', 'update', 'delete'] },
  { resource: 'tribe',         label: 'Tribes',         actions: ['read', 'update', 'delete'] },
  { resource: 'report',        label: 'Reports',        actions: ['read', 'update'] },
  { resource: 'category',      label: 'Categories',     actions: ['read', 'create', 'update'] },
  { resource: 'admin',         label: 'Admins',         actions: ['read', 'create', 'update'] },
  { resource: 'role',          label: 'Roles',          actions: ['read', 'create', 'update', 'delete'] },
  { resource: 'audit',         label: 'Audit Logs',     actions: ['read'] },
  { resource: 'support',       label: 'Support',        actions: ['read', 'create', 'update'] },
  { resource: 'policy',        label: 'Policies',       actions: ['read', 'create', 'update'] },
  { resource: 'challenge',     label: 'Challenges',     actions: ['read', 'create', 'update', 'delete'] },
  { resource: 'submission',    label: 'Submissions',    actions: ['read', 'update'] },
  { resource: 'wallet',        label: 'Wallets',        actions: ['read', 'update'] },
  { resource: 'payout',        label: 'Payouts',        actions: ['read', 'update'] },
  { resource: 'referral',      label: 'Referrals',      actions: ['read', 'create', 'update'] },
  { resource: 'banner',        label: 'Banners',        actions: ['read', 'create', 'update', 'delete'] },
  // 'write' covers both create and update for notifications (matches backend @RequirePermission('notifications', 'write'))
  { resource: 'notifications', label: 'Notifications',  actions: ['read', 'write', 'delete'] },
];

// All columns rendered in the RolePermissionMatrix table (superset of all possible actions)
export const MATRIX_COLUMNS: PermissionAction[] = ['read', 'create', 'update', 'delete', 'write'];

function buildPermissionsFromMatrix(): Record<string, string> {
  const result: Record<string, string> = {};
  for (const { resource, actions } of PERMISSION_MATRIX) {
    const prefix = resource.toUpperCase().replace(/-/g, '_');
    for (const action of actions) {
      result[`${prefix}_${action.toUpperCase()}`] = `${resource}:${action}`;
    }
  }
  // Special wildcard — not derived from the matrix
  result['FULL_ACCESS'] = '*:*';
  return result;
}

export const PERMISSIONS = buildPermissionsFromMatrix();
